/**
 * VenueDefaultsCard: the default slides of a venue, on the Slides screen.
 *
 * Every new game night at the venue starts with a copy of these. The host
 * can then remove or add slides for one night on the Game Night hub. A
 * change here does not change a night that already exists.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Badge, Box, Button, Card, Flex, Heading, Select, Text, TextField } from '@radix-ui/themes';
import api, { fetchVenues } from '../../utils/api';
import type { Venue } from '../../types';
import {
  ANCHOR_OPTIONS,
  customSlideLabel,
  placementLabel,
  type SlideTemplate,
} from '../../utils/customSlides';
import type { SlideAnchor } from '../../utils/slideSequence';

interface VenueDefault {
  id: string;
  anchor: SlideAnchor;
  roundNumber: number | null;
  sortOrder: number;
  template: SlideTemplate;
}

export default function VenueDefaultsCard({ templates }: { templates: SlideTemplate[] }) {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [venueId, setVenueId] = useState('');
  const [defaults, setDefaults] = useState<VenueDefault[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [anchor, setAnchor] = useState<SlideAnchor>('START');
  const [roundNumber, setRoundNumber] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchVenues()
      .then((res) => {
        const list = res.data ?? [];
        setVenues(list);
        if (list[0]) setVenueId(String(list[0].id));
      })
      .catch(() => setVenues([]));
  }, []);

  const loadDefaults = useCallback(async () => {
    if (!venueId) return;
    try {
      const res = await api.get('/api/slides/venue-defaults', { params: { venueId } });
      setDefaults(res.data?.data?.defaults ?? []);
    } catch {
      toast.error('Failed to load venue default slides');
    }
  }, [venueId]);

  useEffect(() => { loadDefaults(); }, [loadDefaults]);

  const needsRound = ANCHOR_OPTIONS.find((o) => o.value === anchor)?.needsRound ?? false;
  const roundOk = !needsRound || (Number.isInteger(Number(roundNumber)) && Number(roundNumber) >= 1);
  const canAdd = Boolean(venueId && templateId) && roundOk && !busy;

  const add = async () => {
    if (!canAdd) return;
    setBusy(true);
    try {
      await api.post('/api/slides/venue-defaults', {
        venueId: Number(venueId),
        templateId,
        anchor,
        roundNumber: needsRound ? Number(roundNumber) : null,
        sortOrder: defaults.length,
      });
      setTemplateId('');
      await loadDefaults();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      toast.error(e.response?.data?.message || 'Failed to add default slide');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    setBusy(true);
    try {
      await api.delete(`/api/slides/venue-defaults/${id}`);
      await loadDefaults();
    } catch {
      toast.error('Failed to remove default slide');
    } finally {
      setBusy(false);
    }
  };

  if (venues.length === 0) return null;

  return (
    <Flex direction="column" gap="3">
      <Box>
        <Heading size="3">Venue defaults</Heading>
        <Text size="2" color="gray">
          Every new game night at a venue starts with these slides. Change a single night on its hub.
        </Text>
      </Box>
      <Card>
        <Flex direction="column" gap="3">
          <Select.Root value={venueId} onValueChange={setVenueId}>
            <Select.Trigger aria-label="Venue" style={{ maxWidth: 360 }} />
            <Select.Content>
              {venues.map((v) => (
                <Select.Item key={v.id} value={String(v.id)}>{v.name}</Select.Item>
              ))}
            </Select.Content>
          </Select.Root>

          {defaults.length === 0 ? (
            <Text size="2" color="gray">No default slides for this venue.</Text>
          ) : (
            <Flex direction="column" gap="2">
              {defaults.map((d) => (
                <Flex key={d.id} align="center" gap="3" justify="between" wrap="wrap">
                  <Flex align="center" gap="2">
                    <Text size="2" weight="medium">{customSlideLabel(d.template)}</Text>
                    <Badge variant="soft" color="gray">{placementLabel(d)}</Badge>
                  </Flex>
                  <Button size="1" variant="ghost" color="red" disabled={busy} onClick={() => remove(d.id)}>
                    Remove
                  </Button>
                </Flex>
              ))}
            </Flex>
          )}

          {templates.length > 0 && (
            <Flex gap="2" wrap="wrap" align="center">
              <Select.Root value={templateId} onValueChange={setTemplateId}>
                <Select.Trigger placeholder="Pick a slide…" aria-label="Default slide" />
                <Select.Content>
                  {templates.map((t) => (
                    <Select.Item key={t.id} value={t.id}>{t.name}</Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
              <Select.Root value={anchor} onValueChange={(v) => setAnchor(v as SlideAnchor)}>
                <Select.Trigger aria-label="Position" />
                <Select.Content>
                  {ANCHOR_OPTIONS.map((o) => (
                    <Select.Item key={o.value} value={o.value}>{o.label}</Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
              {needsRound && (
                <TextField.Root
                  type="number"
                  min={1}
                  value={roundNumber}
                  onChange={(e) => setRoundNumber(e.target.value)}
                  placeholder="Round"
                  aria-label="Round number"
                  style={{ width: '6rem' }}
                />
              )}
              <Button size="2" onClick={add} disabled={!canAdd}>Add default</Button>
            </Flex>
          )}
        </Flex>
      </Card>
    </Flex>
  );
}
